/**
 * notificationQueueBridge.js
 *
 * Small eager-loadable module split off from NotificationQueue.jsx so
 * Header (which mounts on every route) can import the PWA-install
 * bridge helpers without dragging the full 690-line NotificationQueue
 * chunk into the main JS bundle. NotificationQueue itself is
 * React.lazy()'d in App.js (PERF Phase 2, Feb 2026).
 *
 * The bridge functions here talk to NotificationQueue via
 * `window.__acReopenPwaInstall__` and `window.__acQueuedBIP__`. Both
 * are safe no-ops before the queue mounts.
 */

// ── PWA install-prompt pre-capture ────────────────────────────────────
//
// The Chromium `beforeinstallprompt` event can fire at any time after
// page load — including BEFORE the NotificationQueue lazy chunk finishes
// downloading. Missing that event would silently kill the PWA install
// flow (browsers only fire it once per session). We capture it here at
// module load, `preventDefault()` to stop the browser's default mini-
// infobar, and stash the event(s) on `window.__acQueuedBIP__` for
// NotificationQueue to hydrate from on mount.
//
// Idempotent + SSR-safe (guards `typeof window`).
export function installBIPPreCapture() {
  if (typeof window === "undefined") return;
  if (window.__acBIPCaptureInstalled__) return;
  window.__acBIPCaptureInstalled__ = true;
  window.__acQueuedBIP__ = window.__acQueuedBIP__ || [];
  window.addEventListener("beforeinstallprompt", (e) => {
    // Prevent the default mini-infobar (matches NotificationQueue's own
    // handler behaviour post-mount). The event object stays live: we
    // can `.prompt()` on it later from the notification tile.
    e.preventDefault();
    window.__acQueuedBIP__.push(e);
  });
}

// ── isStandalone (shared) ─────────────────────────────────────────────
// Used by both the bridge (below) and NotificationQueue's internal
// tile logic. Duplicated locally in NotificationQueue.jsx too — kept
// small so it stays trivially in-sync.
function isStandalone() {
  if (typeof window === "undefined") return false;
  try {
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
    if (window.navigator && window.navigator.standalone === true) return true;
  } catch {
    // ignore
  }
  return false;
}

// ── Reopen bridge for the Admissions nav ─────────────────────────────
//
// The "Download App" link in the Admissions nav calls this to push the
// PWA install tile back to the front of the notification queue. When
// NotificationQueue has mounted it registers a callback on
// `window.__acReopenPwaInstall__`; before that this is a no-op.
export function reopenPwaInstallTile() {
  if (typeof window === "undefined") return;
  if (typeof window.__acReopenPwaInstall__ === "function") {
    window.__acReopenPwaInstall__();
  }
}

// ── shouldShowInstallAppNavLink ──────────────────────────────────────
//
// Returns true if the "Download App" link in the Admissions nav should
// be visible. Hidden only when we're actually running inside the
// installed standalone app (no point asking you to download what
// you're already in). Otherwise always visible — even if we previously
// recorded an install attempt on this device, we still want the user
// to be able to reach the install flow from the menu.
export function shouldShowInstallAppNavLink() {
  if (typeof window === "undefined") return false;
  if (isStandalone()) return false;
  return true;
}
